import $ from 'jquery';
import i18n, { t }  from '../../i18n/i18n.js';
import DGTable from 'jquery.dgtable';
import View from '../../../controls/View.js';
import DropDownMenu from '../../../controls/DropDownMenu.js';
import NTP from '../../../utils/NTP.js';
import ResizeManager from '../../../utils/ResizeManager.js';
import app from '../../App.js';
import TableContextMenuUtil from '../../../appHelpers/TableContextMenu.js';
import TimeFormatters from '../../../appHelpers/TimeFormatters.js';

const openEndedTimeSpan = function (timeFrom, timeTo) {
    const nowDate = NTP.date();
    return (timeFrom && (!timeTo || timeTo < timeFrom) ? (nowDate - Math.max(timeFrom, nowDate.dateWithZeroTime())) / 1000 : 0);
};

/**
 * These are the fields that are involved in determining the status. When they change - we update the status
 */
const FIELDS_INVOLVED_IN_STATUS = {
    'last_call_dial_time': true,
    'last_call_answer_time': true,
    'last_call_end_time': true,
    'break_start_time': true,
    'last_break_start_time': true,
    'last_break_end_time': true,
    'last_login_time': true,
    'last_logout_time': true,
};

/**
 * These are the fields that are involved in determining the offline status
 */
const FIELDS_INVOLVED_IN_OFFLINE_STATUS = {
    'last_login_time': true,
    'last_logout_time': true,
};

/**
 * These are the fields that needs to be automatically converted to Date objects
 * @type {string[]}
 */
const DATE_FIELDS = [
    'last_call_dial_time', 'last_call_answer_time', 'last_call_end_time',
    'break_start_time',
    'last_break_start_time', 'last_break_end_time',
    'last_login_time', 'last_logout_time',
];

const DATE_FIELDS_MAP = {};
for (const key of DATE_FIELDS)
    DATE_FIELDS_MAP[key] = true;

/** @class RealtimeUserInfoReportPage */
const RealtimeUserInfoReportPage = View.extend(
    /** @lends RealtimeUserInfoReportPage.prototype */
    {

        className: 'main-body',

        events: {
            'change #realtime-toggle-offline': 'onShowOfflineChange'
        },

        /** @constructs */
        initialize() {

            // Add the title for this screen
            let $title = $('<div class="main-title">').appendTo(this.$el);
            $('<h1 class="main">').text(t('realtime_userinfo_report.title')).appendTo($title);

            // Setup the space for action buttons
            let $buttonsBar = $('<div class="action-buttons">').append(
                $('<button class="action-refresh-button">').attr('title', t('refresh')).on('click', this.reload.bind(this))
            ).appendTo($title);

            // Load settings
            this.showOffline = app.getUserSetting('realtime_users_show_offline') || false;

            // Add the "show offline" toggle
            this.$showOfflineToggle = $('<div class="action-toggle-with-label">').append(
                $('<label for="realtime-toggle-offline">').text(t('realtime_userinfo_report.show_offline')),
                $('<div class="boolean-toggle">').append(
                    $('<input id="realtime-toggle-offline" type="checkbox">').prop('checked', this.showOffline),
                    $('<label for="realtime-toggle-offline">')
                )
            ).appendTo($buttonsBar);

            // Track a day change. When a day has changed, we have completely new stats for the day, which needs to be retrieved freshly from the server.
            this._currentDate = NTP.date().zeroTime();
            this._currentDateString = this._currentDate.toLocalISODateString();
            this._dayChangeInterval = this.setInterval(() => {

                let newDate = NTP.date().zeroTime();
                if (+this._currentDate !== +newDate && new Date().getMinutes() >= 1) {
                    this._currentDate = newDate;
                    this._currentDateString = this._currentDate.toLocalISODateString();
                    this.reload();
                }

            }, 60000);

            // Setup the space for the table, and its automatic adjusting to its frame
            this.$tableWrapper = $('<div class="table-wrapper">').appendTo(this.$el);
            this.setupTable();
            this.table.render();
            this.listenTo(ResizeManager, 'resize', this.adjustTableFrame);
            this.setTimeout(() => { this.adjustTableFrame(); }, 0);

            // Make the first connection attempt to the WebSocket
            this.reconnect();
        },

        reconnect() {
            this.disconnect(true);

            let conn = this._conn = new WebSocket(app.wsUrl('realtime_userinfo'));
            conn.addEventListener('message', event => {
                let message = JSON.parse(event.data);

                switch (message['msg']) {

                    case 'authenticated':
                    {
                        this.reload();

                        if (this._clockInterval) {
                            this.clearInterval(this._clockInterval);
                        }
                        this._clockInterval = this.setInterval(() => {
                            this.table.refreshAllVirtualRows();
                        }, 1000);
                    }
                        break;

                    case 'list':
                    {
                        this._data = message['rows'];
                        this._dataMapped = {};

                        let data = this._data;
                        for (let i = 0; i < data.length; i++) {
                            const row = data[i];
                            this._convertRowTimes(row);
                            this._updateRowStatuses(row);
                            this._dataMapped[row['id']] = row;
                        }

                        this.setNeedsFilterOfflineRows();
                        this.setNeedsRefreshRows();

                        this.dataChanged();
                    }
                        break;

                    case 'update':
                    {
                        this._updateField(message['user_id'], message['field'], message['value']);
                        const row = this._dataMapped[message['user_id']];
                        this._convertRowTimes(row);
                        if (FIELDS_INVOLVED_IN_STATUS.hasOwnProperty(message['field'])) {
                            this._updateRowStatuses(row);
                        }
                        if (FIELDS_INVOLVED_IN_OFFLINE_STATUS.hasOwnProperty(message['field'])) {
                            this.setNeedsFilterOfflineRows();
                        }
                        this.dataChanged();
                    }
                        break;

                    case 'updates':
                    {
                        let updates = message['updates'],
                            shouldUpdateStatuses = false,
                            shouldUpdateOfflineStatus = false;
                        if (updates) {
                            for (let key in updates) {
                                if (!updates.hasOwnProperty(key)) continue;
                                this._updateField(message['user_id'], key, updates[key]);
                                shouldUpdateStatuses = shouldUpdateStatuses || FIELDS_INVOLVED_IN_STATUS.hasOwnProperty(key);
                                shouldUpdateOfflineStatus = shouldUpdateOfflineStatus || FIELDS_INVOLVED_IN_OFFLINE_STATUS.hasOwnProperty(key);
                            }
                            const row = this._dataMapped[message['user_id']];
                            this._convertRowTimes(row);
                            if (shouldUpdateStatuses) {
                                this._updateRowStatuses(row);
                            }
                            if (shouldUpdateOfflineStatus) {
                                this.setNeedsFilterOfflineRows();
                            }
                            this.dataChanged();
                        }
                    }
                        break;

                    case 'user_removed':
                    {
                        const userId = message['user_id'];
                        if (this._dataMapped.hasOwnProperty(userId)) {
                            this._data.remove(this._dataMapped[userId]);
                            delete this._dataMapped[userId];
                        }
                        this.setNeedsRefreshRows();
                        this.dataChanged();
                    }
                        break;

                    case 'user_added':
                    {
                        const row = message['row'];
                        const userId = row['id'];
                        if (this._dataMapped.hasOwnProperty(userId)) {
                            this._data.remove(this._dataMapped[userId]);
                            delete this._dataMapped[userId];
                        }
                        this._convertRowTimes(row);
                        this._updateRowStatuses(row);
                        this._data.push(row);
                        this._dataMapped[userId] = row;
                        this.setNeedsRefreshRows();
                        this.dataChanged();
                    }
                        break;

                    case 'error':
                    {
                        let showError = conn === this._conn;

                        conn._bypassDisconnect = true;
                        conn.close();

                        if (showError) {
                            let errorCode = message['error'],
                                errorCodes = t('error_codes'),
                                msg = errorCodes['default'];

                            if (errorCode === 'authorization-error') {
                                window.location = '/admin/login';
                                return;
                            }

                            if (errorCode && errorCodes[errorCode]) {
                                msg = errorCodes[errorCode];
                            }

                            msg = t('realtime_userinfo_report.connection_close_error', {'error': msg});

                            app.AlertUI.confirm(msg, null, () => {
                                this.reconnect();
                            }, null, null);
                        }

                    }
                        break;
                }

            });
            conn.addEventListener('close', _event => {

                if (this._conn === conn) {
                    this._conn = null;

                    if (this._clockInterval) {
                        this.clearInterval(this._clockInterval);
                        this._clockInterval = null;
                    }
                }
                if (conn._bypassDisconnect) return;

                app.AlertUI.confirm(t('realtime_userinfo_report.connection_close'), null, () => {
                    this.reconnect();
                }, null, null);
            });

            return this;
        },

        disconnect(bypass) {
            if (this._conn) {
                if (bypass) {
                    this._conn._bypassDisconnect = true;
                }
                this._conn.close();
                this._conn = null;
            }

            return this;
        },

        reload() {
            if (this._conn) {
                this._conn.send(JSON.stringify({ 'msg': 'list' }))
            } else {
                this.reconnect();
            }

            return this;
        },

        _updateField(userId, field, value) {
            const row = this._dataMapped[userId];
            if (value && typeof value === 'object' && !(value instanceof Date)) {
                if (Array.isArray(value)) {
                    for (let i = 0; i < value.length; i++) {
                        const singleValue = value[i];
                        if (singleValue['local_date'] === this._currentDateString) {
                            if (row[field]) {
                                row[field] += singleValue['delta'];
                            } else {
                                row[field] = singleValue['delta'];
                            }
                        }
                    }
                } else {
                    if (value['local_date'] === this._currentDateString) {
                        if (row[field]) {
                            row[field] += value['delta'];
                        } else {
                            row[field] = value['delta'];
                        }
                    }
                }
            } else {
                row[field] = value;
            }

            if (DATE_FIELDS_MAP.hasOwnProperty(field) && row[field]) {
                row[field] = new Date(row[field]);
            }

            return this;
        },

        _updateRowStatuses(row) {

            let lastStatusUpdate = [
                +row['last_call_dial_time'], +row['last_call_answer_time'], +row['last_call_end_time'],
                +row['last_break_start_time'], +row['last_break_end_time'],
                +row['break_start_time'],
                +row['last_login_time'], +row['last_logout_time'],
            ].filter(val => !isNaN(val))
                .sort()
                .reverse()[0];

            if (!row['last_login_time'] || row['last_logout_time'] > row['last_login_time']) {

                row['status'] = 'logged_out';

            } else if (row['break_start_time']) {

                row['status'] = row['break_type'] + '_break';

            } else if (row['last_login_mode'] === 'override') {

                row['status'] = 'override';
                lastStatusUpdate = row['last_login_time'];

            } else if (lastStatusUpdate && lastStatusUpdate === +row['last_call_end_time']) {

                row['status'] = 'idle';

            } else if (lastStatusUpdate && lastStatusUpdate === +row['last_call_answer_time']) {

                row['status'] = 'in_call';

            } else if (lastStatusUpdate && lastStatusUpdate === +row['last_call_dial_time']) {

                row['status'] = 'dialing';

            } else {

                row['status'] = 'idle';
            }

            if (lastStatusUpdate) {
                lastStatusUpdate = new Date(lastStatusUpdate);
            } else {
                lastStatusUpdate = null;
            }

            row['last_status_update'] = lastStatusUpdate;

            return this;
        },

        _convertRowTimes(row) {
            let i = 0;
            const len = DATE_FIELDS.length;
            let key;
            for (; i < len; i++) {
                key = DATE_FIELDS[i];
                if (row[key]) {
                    row[key] = new Date(row[key]);
                }
            }
            return this;
        },

        setNeedsFilterOfflineRows() {
            this._needsFilterOfflineRows = true;
            return this;
        },

        setNeedsRefreshRows() {
            this._needsRefreshRows = true;
            return this;
        },

        dataChanged() {
            if (this._needsFilterOfflineRows) {
                this._dataOnlineOnly = this._data.filter(item => item['status'] !== 'logged_out');
                this._needsFilterOfflineRows = false;
                this._needsRefreshRows = true;
            }

            if (this._needsRefreshRows) {
                this.table.setRows(this.showOffline ? this._data : this._dataOnlineOnly, true);
                this._needsRefreshRows = false;
            } else {
                this.table.refreshAllVirtualRows();
            }

            return this;
        },

        onShowOfflineChange(evt) {
            this.showOffline = !!evt.target.checked;
            app.setUserSetting('realtime_users_show_offline', this.showOffline);
            this.setNeedsRefreshRows();
            this.dataChanged();
        },

        setupTable() {
            const columns = [
                {name: 'user_fullname', label: t('realtime_userinfo_report.columns.user_fullname')},
                {name: 'username', label: t('realtime_userinfo_report.columns.username')},
                {name: 'team_name', label: t('realtime_userinfo_report.columns.team_name')},
                {name: 'status', label: t('realtime_userinfo_report.columns.status')},
                {name: 'status_duration', label: t('realtime_userinfo_report.columns.status_duration')},
                {name: 'call_count', label: t('realtime_userinfo_report.columns.call_count')},
                {name: 'answered_call_count', label: t('realtime_userinfo_report.columns.answered_call_count')},
                {name: 'saved_sessions', label: t('realtime_userinfo_report.columns.saved_sessions')},
                {name: 'closed_orders', label: t('realtime_userinfo_report.columns.closed_orders')},
                {name: 'net_call_duration', label: t('realtime_userinfo_report.columns.net_call_duration')},
                {name: 'gross_call_duration', label: t('realtime_userinfo_report.columns.gross_call_duration')},
                {name: 'call_duration_avg', label: t('realtime_userinfo_report.columns.call_duration_avg')},
                {name: 'personal_break_duration', label: t('realtime_userinfo_report.columns.personal_break_duration')},
                {name: 'logged_in_duration', label: t('realtime_userinfo_report.columns.logged_in_duration')},
            ];

            const tableConfigKey = 'realtime';
            app.TableAutoSavingConfigUtil.load(columns, tableConfigKey);

            const tStatuses = t('realtime_userinfo_report.statuses');

            const table = this.table = new DGTable({
                el: this.$tableWrapper[0],
                columns: columns,
                width: DGTable.Width.SCROLL,
                convertColumnWidthsToRelative: true,
                cellFormatter: (val, colName, row) => {
                    if (colName === 'user_fullname') {

                        return (row['first_name'] || '') + ' ' + (row['last_name'] || '');

                    } else if (colName === 'status') {

                        return tStatuses[val] || '';

                    } else if (colName === 'status_duration') {

                        val = row['last_status_update'];
                        if (!val || row['status'] === 'logged_out') return '';

                        return TimeFormatters.timeFromSeconds((NTP.now() - val) / 1000);

                    } else if (colName === 'call_duration_avg') {

                        return TimeFormatters.timeFromSeconds(Math.round((row['net_call_duration'] / row['answered_call_count']) || 0));

                    } else if (colName === 'gross_call_duration') {

                        return TimeFormatters.timeFromSeconds(row['gross_call_duration']);

                    } else if (colName === 'net_call_duration') {

                        return TimeFormatters.timeFromSeconds(row['net_call_duration']);

                    } else if (colName === 'personal_break_duration') {

                        row['total_personal_break_duration'] = row['personal_break_duration'] +
                            (row['break_type'] === 'personal' ? openEndedTimeSpan(row['break_start_time']) : 0);

                        return TimeFormatters.timeFromSeconds(row['total_personal_break_duration']);

                    } else if (colName === 'logged_in_duration') {

                        return TimeFormatters.timeFromSeconds(row['logged_in_duration'] +
                            openEndedTimeSpan(row['last_login_time'], row['last_logout_time']));

                    } else if (colName === 'call_count') {

                        return '<a href="/admin/reports/call_events?filters=' + encodeURIComponent(btoa(encodeURIComponent(JSON.stringify({
                            'user_id': {
                                'value': [
                                    {
                                        'value': row['id'],
                                        'label': ((row['first_name'] || '') + ' ' + (row['last_name'] || '')).trim(),
                                    },
                                ],
                            },
                        })))) + '">' + val + '</a>';
                    }
                    return val;
                },
                comparatorCallback: (columnName, descending) => {
                    const leftValue = descending ? 1 : -1,
                        rightValue = descending ? -1 : 1;
                    if (columnName === 'user_fullname') {
                        return (a, b) => {
                            if (a['first_name'] < b['first_name']) return leftValue;
                            if (a['first_name'] > b['first_name']) return rightValue;
                            if (a['last_name'] < b['last_name']) return leftValue;
                            if (a['last_name'] > b['last_name']) return rightValue;
                            return 0;
                        };
                    } else if (columnName === 'status_duration') {
                        return (a, b) => {
                            if (a['last_status_update'] < b['last_status_update']) return leftValue;
                            if (a['last_status_update'] > b['last_status_update']) return rightValue;
                            return 0;
                        };
                    } else if (columnName === 'call_duration_avg') {
                        return (a, b) => {
                            a = (a['net_call_duration'] / a['answered_call_count']) || 0;
                            b = (b['net_call_duration'] / b['answered_call_count']) || 0;
                            if (a < b) return leftValue;
                            if (a > b) return rightValue;
                            return 0;
                        };
                    } else if (columnName === 'personal_break_duration') {
                        return (a, b) => {
                            a = a['personal_break_duration'] +
                                (a['break_type'] === 'personal' ? openEndedTimeSpan(a['break_start_time']) : 0);
                            b = b['personal_break_duration'] +
                                (b['break_type'] === 'personal' ? openEndedTimeSpan(b['break_start_time']) : 0);
                            if (a < b) return leftValue;
                            if (a > b) return rightValue;
                            return 0;
                        };
                    } else if (columnName === 'logged_in_duration') {
                        return (a, b) => {
                            a = a['logged_in_duration'] + openEndedTimeSpan(a['last_login_time'], a['last_logout_time']);
                            b = b['logged_in_duration'] + openEndedTimeSpan(b['last_login_time'], b['last_logout_time']);
                            if (a < b) return leftValue;
                            if (a > b) return rightValue;
                            return 0;
                        };
                    }
                    return null;
                },
            });

            app.TableAutoSavingConfigUtil.setup(table, tableConfigKey);

            table
                .on('rowcreate', (filteredIndex, index, row, rowData) => {

                row.className += index % 2 ? ' alt-row' : ' normal-row';

                if (rowData['status'] === 'logged_out') {
                    row.className += ' logged_out';
                }

                const now = NTP.now();

                if (rowData['status'] === 'idle' &&
                    rowData['idle_duration_alert_minutes'] > 0) {

                    const idleTime = [
                        +rowData['last_call_end_time'],
                        +rowData['last_break_end_time'],
                        +rowData['last_login_time'],
                    ].filter(val => !isNaN(val))
                        .sort()
                        .reverse()[0];

                    if ((now - idleTime) / 60000 > rowData['idle_duration_alert_minutes']) {
                        $(row).find('.dgtable-cell[data-column=status_duration]').addClass('alert');
                    }
                }

                if (rowData['status'] === 'in_call' &&
                    rowData['in_call_duration_alert_minutes'] > 0 &&
                    (now - +rowData['last_call_answer_time']) / 60000 > rowData['in_call_duration_alert_minutes']) {

                    $(row).find('.dgtable-cell[data-column=status_duration]').addClass('alert');
                }

                if (rowData['status'] === 'dialing' &&
                    rowData['dialing_duration_alert_minutes'] > 0 &&
                    (now - +rowData['last_call_dial_time']) / 60000 > rowData['dialing_duration_alert_minutes']) {

                    $(row).find('.dgtable-cell[data-column=status_duration]').addClass('alert');
                }

                if (rowData['status'] !== 'logged_out' &&
                    rowData['logged_in_duration_alert_minutes'] > 0 &&
                    (now - +rowData['last_login_time']) / 60000 > rowData['logged_in_duration_alert_minutes']) {

                    $(row).find('.dgtable-cell[data-column=logged_in_duration]').addClass('alert');
                }

                if (rowData['personal_break_minutes'] > 0 &&
                    rowData['total_personal_break_duration'] / 60 > rowData['personal_break_minutes']) {

                    $(row).find('.dgtable-cell[data-column=personal_break_duration]').addClass('alert');
                }

            })
                .on('headercontextmenu', (columnName, pageX, pageY, _cellBounds) => {
                TableContextMenuUtil.setup(table, pageX, pageY);
                $.disableContextMenuForCurrentEvent();
            })
                .on('cellpreview', (previewCell, physicalRowIndex, columnName, rowData) => {

                    if (!physicalRowIndex) return;

                    $(previewCell).on('mouseup', evt => {
                        if (evt.which !== 3) return;

                        evt.preventDefault();
                        $.disableContextMenuForCurrentEvent();
                        this.showActionsForRowAtPoint(rowData, evt.pageX, evt.pageY);
                    });

                });

            this.$tableWrapper.on('mouseup', evt => {

                if (evt.which !== 3) return;

                const $row = $(evt.target).closest('.dgtable-row');
                if (!$row[0]) return;

                const rowData = table.getDataForRow($row[0].physicalRowIndex);

                evt.preventDefault();
                $.disableContextMenuForCurrentEvent();
                this.showActionsForRowAtPoint(rowData, evt.pageX, evt.pageY);

            });

            return this;
        },

        adjustTableFrame() {
            if (this.table) {
                const $offsetParent = this.$tableWrapper.offsetParent();
                const offsetTop = this.$tableWrapper.offset().top - $offsetParent.offset().top;
                const lastTableSize = this._lastTableSize;
                const tableSize = {width: $offsetParent.width(), height: $offsetParent.height() - offsetTop - 20};
                this.table.$el.css({'width': '100%', 'height': tableSize.height});
                if (!lastTableSize || tableSize.height !== lastTableSize.height) {
                    this.table.tableHeightChanged();
                } else if (!lastTableSize || tableSize.width !== lastTableSize.width) {
                    this.table.tableWidthChanged();
                }
                this._lastTableSize = tableSize;
            }
            return this;
        },

        render() {
            return this;
        },

        showActionsForRowAtPoint(rowData, x, y) {
            if (rowData['status'] === 'logged_out') return this;

            const menu = new DropDownMenu({className: 'dropdown-menu dropdown-menu-table-context-actions'});

            if (rowData['status'] !== 'personal_break') {
                menu.addItem('personal_break', t('realtime_userinfo_report.context_actions.personal_break'));
            }
            if (rowData['status'] !== 'official_break') {
                menu.addItem('official_break', t('realtime_userinfo_report.context_actions.official_break'));
            }
            if (rowData['status'] === 'personal_break' ||
                rowData['status'] === 'official_break') {
                menu.addItem('stop_break', t('realtime_userinfo_report.context_actions.stop_break'));
            }
            menu.addItem('logout', t('realtime_userinfo_report.context_actions.logout'));

            menu.prepareShow().$el.show().focus();

            const repositionOptions = {
                targetOffset: {left: x, top: y},
                targetHeight: 0,
                targetWidth: 0,
                targetRtl: false,
                anchor: 'start bottom',
                position: 'start top',
                updateWidth: false,
            };

            menu.repositionAndResize(repositionOptions);

            const boundRepositionAndResize = () => {
                menu.repositionAndResize(repositionOptions);
            };

            $(window).on('resize', boundRepositionAndResize);

            const hideMenu = () => {
                menu.remove();
                $(window).off('resize', boundRepositionAndResize);
            };

            menu.on('select', ({item}) => {

                switch (item.value) {

                    case 'logout':

                        app.AlertUI.showLoader(t('activity_indicator.force_logging_out'));

                        this.ajaxJsonPost(app.apiUrl('users/' + rowData['id'] + '/logout'), {})
                            .always(() => {
                                app.AlertUI.hideLoader();
                            })
                            .fail((xhr, error) => {
                                app.showXhrError(xhr, error);
                            });

                        break;

                    case 'personal_break':

                        app.AlertUI.showLoader(t('activity_indicator.starting_break'));

                        this.ajaxJsonPost(app.apiUrl('users/' + rowData['id'] + '/start_break'), {
                            'type': 'personal'
                        })
                            .always(() => {
                                app.AlertUI.hideLoader();
                            })
                            .fail((xhr, error) => {
                                app.showXhrError(xhr, error);
                            });

                        break;

                    case 'official_break':

                        app.AlertUI.showLoader(t('activity_indicator.starting_break'));

                        this.ajaxJsonPost(app.apiUrl('users/' + rowData['id'] + '/start_break'), {
                            'type': 'official'
                        })
                            .always(() => {
                                app.AlertUI.hideLoader();
                            })
                            .fail((xhr, error) => {
                                app.showXhrError(xhr, error);
                            });

                        break;

                    case 'stop_break':

                        app.AlertUI.showLoader(t('activity_indicator.stopping_break'));

                        this.ajaxJsonPost(app.apiUrl('users/' + rowData['id'] + '/stop_break'), {})
                            .always(() => {
                                app.AlertUI.hideLoader();
                            })
                            .fail((xhr, error) => {
                                app.showXhrError(xhr, error);
                            });

                        break;

                    case 'live_listening':

                        app.AlertUI.showLoader(t('activity_indicator.starting_live_listening'));

                        this.ajaxJsonPost(app.apiUrl('users/' + rowData['id'] + '/listen'), {})
                            .always(() => {
                                app.AlertUI.hideLoader();
                            })
                            .then(data => {
                                //TODO: Popup or some other indicator
                            })
                            .fail((xhr, error) => {
                                app.showXhrError(xhr, error);
                            });

                        break;

                    case 'live_screen_monitor':

                        app.AlertUI.showLoader(t('activity_indicator.starting_live_screen_monitor'));

                        this.ajaxJsonPost(app.apiUrl('users/' + rowData['id'] + '/screen_monitor'), {})
                            .always(() => {
                                app.AlertUI.hideLoader();
                            })
                            .then(data => {
                                console.log(data);
                                //TODO: Start VNC Session
                            })
                            .fail((xhr, error) => {
                                app.showXhrError(xhr, error);
                            });

                        break;

                }

                hideMenu();
            });

            menu.$el.on('blur', hideMenu);

            return this;
        },

        remove() {
            this.disconnect(true);

            if (this._filters) {
                this._filters.destroy();
            }

            this.constructor.__super__.remove.apply(this, arguments);
        }
    }
);

export default RealtimeUserInfoReportPage;
